import moment from 'moment';
import config from '../config/config';

const dateFormat: string = config.getDateFormat();
const dateFormatServer: string = config.getDateToServer();

/**
 * Type for date input that can be either a string or Date object
 */
type DateInput = string | Date;

/**
 * Interface for date formatting options
 */
interface DateFormatOptions {
  format?: string;
  invalidDateMessage?: string;
}

/**
 * Formats a date using the specified format
 * @param date - The date to format
 * @param options - Formatting options including format and invalid date message
 * @returns Formatted date or invalidDateMessage
 */
export const formatDate = (
  date: DateInput,
  options: DateFormatOptions = {}
): string => {
  const { 
    format = dateFormat, 
    invalidDateMessage = 'Invalid Date' 
  } = options;

  try {
    if (!date) return invalidDateMessage;
    const momentDate = moment(date);
    if (!momentDate.isValid()) return invalidDateMessage;
    return momentDate.format(format);
  } catch (error) {
    console.error('Error in formatDate:', error);
    return invalidDateMessage;
  }
};

/**
 * Formats a date for server communication
 * @param date - The date to format
 * @param invalidDateMessage - Message to return if date is invalid
 * @returns Formatted date or invalidDateMessage
 */
export const formatDateForServer = (
  date: DateInput,
  invalidDateMessage: string = 'Invalid Date'
): string => {
  return formatDate(date, { 
    format: dateFormatServer, 
    invalidDateMessage 
  });
};

/**
 * Interface for date range validation
 */
interface DateRange {
  startDate: DateInput;
  endDate: DateInput;
}

/**
 * Validates if a date is between two other dates
 * @param date - The date to validate
 * @param range - Object containing start and end dates
 * @returns True if date is between start and end dates
 */
export const isDateBetween = (
  date: DateInput,
  range: DateRange
): boolean => {
  const { startDate, endDate } = range;
  
  if (!date || !startDate || !endDate) return false;
  
  const targetDate = moment(date);
  const periodStart = moment(startDate);
  const periodEnd = moment(endDate);
  
  return targetDate.isBetween(periodStart, periodEnd, 'day', '[]');
};

/**
 * Interface for date sorting options
 */
interface DateSortOptions {
  invalidDateMessage?: string;
}

/**
 * Sorts two dates
 * @param a - First date
 * @param b - Second date
 * @param options - Sorting options including invalid date message
 * @returns -1 if a < b, 0 if equal, 1 if a > b
 */
export const sortDates = (
  a: string,
  b: string,
  options: DateSortOptions = {}
): number => {
  const { invalidDateMessage = 'Invalid Date' } = options;

  if (a === invalidDateMessage && b === invalidDateMessage) return 0;
  if (a === invalidDateMessage) return 1;
  if (b === invalidDateMessage) return -1;

  try {
    const a1 = moment(a, dateFormat);
    const b1 = moment(b, dateFormat);

    if (!a1.isValid() && !b1.isValid()) return 0;
    if (!a1.isValid()) return 1;
    if (!b1.isValid()) return -1;

    return a1.valueOf() - b1.valueOf();
  } catch (error) {
    console.error('Error in sortDates:', error);
    return 0;
  }
}; 

export const getDaysLeft = (endDate:string, appointmentStatus:string) => {
  const now = moment();
  const endDateF = moment(endDate);
  
  return ['SCHEDULES_ASSIGNED', 'CREATED'].includes(appointmentStatus) 
    ? Math.ceil(endDateF.diff(now, 'days', true))
    : -1;
}